'use client';

import { c as _c } from "react/compiler-runtime";
import { Button } from '@extern/ui/button';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@extern/ui/form';
import { Input } from '@extern/ui/input';
import { Separator } from '@extern/ui/separator';
import { Text } from '@extern/ui/text';
import { zodResolver } from '@hookform/resolvers/zod';
import Link from 'next/link';
import { useSearchParams } from 'next/navigation';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { AuthCard } from '@/app/(auth)/_partials/auth-card';
import { AuthGoogleButton } from '@/app/(auth)/_partials/auth-google-button';
import { AuthLastUsedButtonWrap } from '@/app/(auth)/_partials/auth-last-used-badge';
import { ExistingAccountNotice } from '@/app/(auth)/_partials/existing-account-notice';
import { isExistingAccountArrival } from '@/app/(auth)/_utils/existing-account-handoff';
import { prefilledEmail } from '@/app/(auth)/_utils/prefilled-email';
import { useUtmParams } from '@/hooks/use-utm-params';
import { emailSchema } from '@/infra/schemas/auth';
import { getAllowlistedSearchParams } from '@/utils/get-allowlisted-search-params';
import { useLastAuthenticationStrategy } from '../../_hooks/use-last-authentication-strategy';
import { useSignIn } from '../_hooks/use-sign-in';
const FormSchema = z.object({
  identifier: emailSchema
});
export function SignInStartControl() {
  const $ = _c(46);
  useUtmParams();
  const searchParams = useSearchParams();
  const {
    onSignInIdentifier,
    onSignInWithGoogle,
    fetchStatus
  } = useSignIn();
  const lastAuthenticationStrategy = useLastAuthenticationStrategy();
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = zodResolver(FormSchema);
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  if ($[1] !== searchParams) {
    t1 = prefilledEmail(searchParams);
    $[1] = searchParams;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] !== t1) {
    t2 = {
      resolver: t0,
      defaultValues: {
        identifier: t1
      },
      mode: "onChange"
    };
    $[3] = t1;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const form = useForm(t2);
  let t3;
  if ($[5] !== onSignInIdentifier) {
    t3 = async function handleSubmit(data) {
      await onSignInIdentifier(data.identifier);
    };
    $[5] = onSignInIdentifier;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const handleSubmit = t3;
  let t4;
  if ($[7] !== form || $[8] !== handleSubmit) {
    t4 = form.handleSubmit(handleSubmit);
    $[7] = form;
    $[8] = handleSubmit;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  let t5;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <AuthCard.Header><AuthCard.Title>Join a Community Committed to Your Career Success</AuthCard.Title><AuthCard.Description>Access company Externships, build skills, and explore careers on your terms.</AuthCard.Description></AuthCard.Header>;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  let t6;
  if ($[11] !== searchParams) {
    t6 = isExistingAccountArrival(searchParams) ? <ExistingAccountNotice /> : null;
    $[11] = searchParams;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  let t7;
  if ($[13] !== form.control) {
    t7 = <FormField control={form.control} name="identifier" render={_temp} />;
    $[13] = form.control;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  let t8;
  if ($[15] !== t6 || $[16] !== t7) {
    t8 = <AuthCard.Content>{t6}{t7}</AuthCard.Content>;
    $[15] = t6;
    $[16] = t7;
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  const t9 = fetchStatus === "fetching";
  const t10 = fetchStatus === "fetching";
  let t11;
  if ($[18] !== t10 || $[19] !== t9) {
    t11 = <Button aria-label="Sign In" className="w-full" data-cy="sign-in" disabled={t9} isLoading={t10} type="submit">Sign in with Email</Button>;
    $[18] = t10;
    $[19] = t9;
    $[20] = t11;
  } else {
    t11 = $[20];
  }
  let t12;
  if ($[21] !== lastAuthenticationStrategy || $[22] !== t11) {
    t12 = <AuthLastUsedButtonWrap matchStrategy="password" strategy={lastAuthenticationStrategy}>{t11}</AuthLastUsedButtonWrap>;
    $[21] = lastAuthenticationStrategy;
    $[22] = t11;
    $[23] = t12;
  } else {
    t12 = $[23];
  }
  let t13;
  if ($[24] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = <div className="relative flex w-full items-center justify-center p-3"><Separator /><span className="absolute mx-auto bg-white px-4 text-gray-dark text-sm">or</span></div>;
    $[24] = t13;
  } else {
    t13 = $[24];
  }
  const t14 = fetchStatus === "fetching";
  let t15;
  if ($[25] !== onSignInWithGoogle || $[26] !== t14) {
    t15 = <AuthGoogleButton isLoading={t14} label="Sign in with Google" onClick={onSignInWithGoogle} />;
    $[25] = onSignInWithGoogle;
    $[26] = t14;
    $[27] = t15;
  } else {
    t15 = $[27];
  }
  let t16;
  if ($[28] !== searchParams) {
    t16 = getAllowlistedSearchParams(searchParams);
    $[28] = searchParams;
    $[29] = t16;
  } else {
    t16 = $[29];
  }
  const t17 = `/sign-up?${t16}`;
  let t18;
  if ($[30] === Symbol.for("react.memo_cache_sentinel")) {
    t18 = <span className="text-blue-dark hover:underline">Sign up</span>;
    $[30] = t18;
  } else {
    t18 = $[30];
  }
  let t19;
  if ($[31] !== t17) {
    t19 = <Text body="lg" className="text-center font-medium">Don't have an account? <Link href={t17}>{t18}</Link></Text>;
    $[31] = t17;
    $[32] = t19;
  } else {
    t19 = $[32];
  }
  let t20;
  if ($[33] !== t12 || $[34] !== t15 || $[35] !== t19) {
    t20 = <AuthCard.Footer>{t12}{t13}{t15}{t19}</AuthCard.Footer>;
    $[33] = t12;
    $[34] = t15;
    $[35] = t19;
    $[36] = t20;
  } else {
    t20 = $[36];
  }
  let t21;
  if ($[37] !== t20 || $[38] !== t8) {
    t21 = <AuthCard>{t5}{t8}{t20}</AuthCard>;
    $[37] = t20;
    $[38] = t8;
    $[39] = t21;
  } else {
    t21 = $[39];
  }
  let t22;
  if ($[40] !== t21 || $[41] !== t4) {
    t22 = <form onSubmit={t4}>{t21}</form>;
    $[40] = t21;
    $[41] = t4;
    $[42] = t22;
  } else {
    t22 = $[42];
  }
  let t23;
  if ($[43] !== form || $[44] !== t22) {
    t23 = <Form {...form}>{t22}</Form>;
    $[43] = form;
    $[44] = t22;
    $[45] = t23;
  } else {
    t23 = $[45];
  }
  return t23;
}
function _temp(t0) {
  const {
    field
  } = t0;
  return <FormItem className="flex flex-col gap-2"><FormLabel>Email address</FormLabel><FormControl><Input autoCapitalize="none" autoComplete="email" autoCorrect="off" data-cy="username" id="username" placeholder="joe@extern.com" type="email" {...field} /></FormControl><FormMessage /></FormItem>;
}